"use client";

import React, { useEffect, useState } from "react";
import { FacebookEmbed, InstagramEmbed, XEmbed } from "react-social-media-embed";

import {
  getFacebookVideoIframeSrc,
  isFacebookShareVideoUrl,
  parseFacebookUrl,
} from "@/utils/facebookEmbedUrl";

const EMBED_COMPONENTS = {
  instagram: InstagramEmbed,
  facebook: FacebookEmbed,
  twitter: XEmbed,
  x: XEmbed,
} as const;

const FacebookEmbedWrapper = ({ url }: { url: string }) => {
  const [embedUrl, setEmbedUrl] = useState(url);
  const parsed = parseFacebookUrl(embedUrl);
  const isVideo = parsed?.kind === "video" || isFacebookShareVideoUrl(url);

  useEffect(() => {
    setEmbedUrl(url);

    if (!isFacebookShareVideoUrl(url)) {
      return;
    }

    fetch(`/api/resolve-facebook-url?url=${encodeURIComponent(url)}`)
      .then(res => (res.ok ? res.json() : null))
      .then(data => {
        if (data?.url) {
          setEmbedUrl(data.url);
        }
      })
      .catch(() => {});
  }, [url]);

  if (isVideo) {
    return (
      <iframe
        src={getFacebookVideoIframeSrc(embedUrl)}
        width="100%"
        height="476"
        className="max-w-full border-0"
        scrolling="no"
        allowFullScreen
        allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
        title="Facebook video"
      />
    );
  }

  return <FacebookEmbed url={embedUrl} className="h-full" width="100%" />;
};

export const SocialEmbed = ({
  url,
  embedType,
  format = "",
}: {
  url: string;
  embedType: string;
  format?: string;
}) => {
  const [isClient, setIsClient] = useState(false);

  const normalizedType =
    (embedType?.toLowerCase() === "x" ? "twitter" : embedType?.toLowerCase()) || "twitter";
  const EmbedComponent = EMBED_COMPONENTS[normalizedType as keyof typeof EMBED_COMPONENTS];
  const canRender = Boolean(url?.trim()) && EmbedComponent;

  useEffect(() => {
    setIsClient(true);
  }, []);

  if (!url?.trim()) {
    return null;
  }

  const wrapperClass =
    "w-full max-w-[550px] min-h-[200px] my-4 " +
    (format === "center" ? "mx-auto" : format === "right" ? "ml-auto" : "");

  if (!canRender) {
    return (
      <div className={wrapperClass}>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primaryColor underline break-all"
        >
          {url}
        </a>
      </div>
    );
  }

  return (
    <div className={wrapperClass}>
      {isClient ? (
        <div className="min-h-[200px] [&_iframe]:max-w-full">
          {normalizedType === "facebook" ? (
            <FacebookEmbedWrapper url={url} />
          ) : (
            <EmbedComponent url={url} className="h-full" width="100%" />
          )}
        </div>
      ) : (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primaryColor underline break-all"
        >
          {url}
        </a>
      )}
    </div>
  );
};

export const YoutubeEmbed = ({
  videoID,
  embedType,
}: {
  videoID: string;
  embedType: "video" | "playlist";
}) => {
  return (
    <div className="w-full aspect-video">
      <iframe
        width="100%"
        height="100%"
        src={
          embedType === "playlist"
            ? `https://www.youtube.com/embed/videoseries?list=${videoID}`
            : `https://www.youtube-nocookie.com/embed/${videoID}`
        }
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen={true}
        title="YouTube video"
      />
    </div>
  );
};
